// src/pages/billing/JournalEntryListPage.tsx

import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { observer } from '@react';
import { useBilling, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Card,
  CardContent,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  Badge,
  ErrorAlert,
} from '@react/components';

function formatDate(dateStr: string | undefined | null): string {
  if (!dateStr) return '--';
  try { return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return String(dateStr); }
}

function statusVariant(status: string): 'success' | 'warning' | 'error' | 'primary' | 'secondary' {
  switch (status) {
    case 'JOURNAL_ENTRY_STATUS_POSTED': return 'success';
    case 'JOURNAL_ENTRY_STATUS_PENDING': return 'warning';
    case 'JOURNAL_ENTRY_STATUS_DRAFT': return 'secondary';
    case 'JOURNAL_ENTRY_STATUS_VOIDED': return 'error';
    default: return 'secondary';
  }
}

function enumLabel(value: string, prefix: string): string {
  return (value || '').replace(prefix, '').replace(/_/g, ' ');
}

export const JournalEntryListPage = observer(() => {
  const store = useBilling();
  const { t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [entries, setEntries] = useState<any[]>([]);

  useEffect(() => {
    store.ListJournalEntries().then((res) => {
      const list = res?.journal_entries?.entries;
      setEntries(Array.isArray(list) ? list : []);
    }).catch(() => {});
  }, [searchParams]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchParams(search ? { search } : {});
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <nav className="text-sm mb-2">
            <Link to="/billing" className="text-muted-foreground hover:text-foreground">Billing</Link>
            <span className="mx-2 text-muted-foreground">/</span>
            <span className="text-foreground">Journal Entries</span>
          </nav>
          <h1 className="text-2xl font-bold text-foreground">Journal Entries</h1>
        </div>
        <Link to="/billing/journal-entries/new"><Button>Create Journal Entry</Button></Link>
      </div>

      <Card>
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className="flex gap-4">
            <Input placeholder={t('common.search')} value={search} onChange={(e) => setSearch(e.target.value)} fullWidth={false} className="flex-1" />
            <Button type="submit" variant="secondary">{t('common.search')}</Button>
          </form>
        </CardContent>
      </Card>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      <Card>
        <Table striped hoverable>
          <TableHeader>
            <TableRow>
              <TableHead>Number</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">{t('common.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {store.isLoading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8">{t('common.loading')}</TableCell></TableRow>
            ) : entries.length === 0 ? (
              <TableEmpty colSpan={6} message="No journal entries found" />
            ) : (
              entries.map((item: any) => (
                <TableRow key={item.entity?.id || Math.random()}>
                  <TableCell>
                    <Link to={`/billing/journal-entries/${item.entity?.id}`} className="text-primary hover:underline">
                      {String(item.number ?? '--')}
                    </Link>
                  </TableCell>
                  <TableCell>{formatDate(item.entry_date)}</TableCell>
                  <TableCell>{enumLabel(item.type, 'JOURNAL_ENTRY_TYPE_')}</TableCell>
                  <TableCell>{String(item.description ?? '--')}</TableCell>
                  <TableCell><Badge variant={statusVariant(item.status)} size="sm">{enumLabel(item.status, 'JOURNAL_ENTRY_STATUS_')}</Badge></TableCell>
                  <TableCell className="text-right">
                    <Link to={`/billing/journal-entries/${item.entity?.id}`}><Button variant="ghost" size="sm">View</Button></Link>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
});

export default JournalEntryListPage;
